import { ArrowBendUpLeftIcon } from '@phosphor-icons/react'
import { Handle, type NodeProps, Position, useNodeConnections, useNodeId } from '@xyflow/react'
import { cn } from 'app/lib/utils'
import { formatDuration } from 'app/lib/workflows/format'
import type { WaitForReplyFlowNode } from 'app/types/workflows'
import { AppendButton } from './AppendButton'
import { NodeShell } from './NodeShell'

const HANDLE = `!h-2.5 !w-2.5 !border-2 !border-(--surface)`

/** Waits for the contact to reply to an upstream conversation, then forks. Reuses the branch
 *  `yes`/`no` handles (Replied / No reply) so all the append/insert/layout logic works unchanged.
 *  Orphan-when-no-upstream-conversation is enforced via the shared issues map (see NodeShell). */
export function WaitForReplyNode({ data, selected }: NodeProps<WaitForReplyFlowNode>) {
  const nodeId = useNodeId()
  const yesOpen = useNodeConnections({ handleType: `source`, handleId: `yes` }).length === 0
  const noOpen = useNodeConnections({ handleType: `source`, handleId: `no` }).length === 0
  const summary = data.within ? `Reply within ${formatDuration(data.within, data.unit ?? `hours`)}` : `Wait for a reply`

  return (
    <NodeShell
      icon={ArrowBendUpLeftIcon}
      accent="violet"
      kind="Wait for reply"
      label={data.label}
      description={summary}
      selected={selected}
      hasSource={false}
    >
      <div className="flex justify-between px-5 pb-2 text-[10px] font-semibold">
        <span className="text-default">REPLIED</span>
        <span className="text-minimal">NO REPLY</span>
      </div>
      <Handle
        id="yes"
        type="source"
        position={Position.Bottom}
        style={{ left: `26%` }}
        className={cn(HANDLE, `!bg-inverse`)}
      />
      <Handle
        id="no"
        type="source"
        position={Position.Bottom}
        style={{ left: `74%` }}
        className={cn(HANDLE, `!bg-inverse/40`)}
      />
      {nodeId != null && yesOpen && (
        <AppendButton nodeId={nodeId} sourceHandle="yes" className="-bottom-8 left-[26%] -translate-x-1/2" />
      )}
      {nodeId != null && noOpen && (
        <AppendButton nodeId={nodeId} sourceHandle="no" className="-bottom-8 left-[74%] -translate-x-1/2" />
      )}
    </NodeShell>
  )
}
